import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, ErrorState } from '@ui-kit';
import { CommonUsagePanel } from '../../_shared/CommonUsagePanel';
import { useHasPermission } from '../../_shared/hooks/useCurrentUser';
import {
  useTenantContext,
  useTenants,
  useRoutingStatus,
  useTenantSamples,
  useSwitchTenant,
  useCreateSample,
} from '../hooks/useTenantSwitcher';
import { TenantSelector } from '../components/TenantSelector';
import { RoutingStatusCard } from '../components/RoutingStatusCard';
import { SampleGrid } from '../components/SampleGrid';
import { SampleFormDialog } from '../components/SampleFormDialog';
import type { ApiError, SampleSearchCondition } from '../types/tenantSwitcher';
import type { TenantSampleCreateFormValues } from '../schema/tenantSwitcherSchema';

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_SORT = 'createdAt,desc';

const COMMON_USAGE_ITEMS = [
  {
    module: 'eraf-data-jpa',
    moduleColor: 'green' as const,
    api: 'TenantContext.get() (C046)',
    role: '현재 테넌트 표시',
    snippet: 'TenantContext.get().orElse(props.getDefaultTenant())',
  },
  {
    module: 'eraf-data-jpa',
    moduleColor: 'green' as const,
    api: 'TenantAwareEntity @TenantId (C046)',
    role: '표본 격리 — INSERT/WHERE 자동',
    snippet: 'class TenantSample extends TenantAwareEntity {}',
  },
  {
    module: 'eraf-data-jpa',
    moduleColor: 'green' as const,
    api: 'TenantProperties (C046)',
    role: '테넌트 설정 패널 노출',
    snippet: 'props.getDefaultTenant()/isRequireTenant()',
  },
  {
    module: 'eraf-data-jpa',
    moduleColor: 'green' as const,
    api: 'DataSourceType (C049)',
    role: '라우팅 판정 라벨',
    snippet: 'RoutingStatusResponse(..., DataSourceType.READ, ...)',
  },
  {
    module: 'eraf-data-jpa',
    moduleColor: 'green' as const,
    api: 'RoutingDataSourceProperties (C049)',
    role: '라우팅 ON/OFF · 노드 표시',
    snippet: 'props.isEnabled(); props.getWrite().getUrl()',
  },
  {
    module: 'eraf-data-jpa',
    moduleColor: 'green' as const,
    api: '@Transactional(readOnly) → RoutingDataSource (C049)',
    role: '조회=READ, 변경=WRITE 라우팅',
    snippet: '@Transactional(readOnly=true) listSamples()',
  },
  {
    module: 'eraf-data-jpa',
    moduleColor: 'green' as const,
    api: '@SensitiveColumn + SensitiveStringConverter (C044)',
    role: 'secret 자동 암복호화',
    snippet: '@SensitiveColumn @Convert(...) String secret;',
  },
  {
    module: 'eraf-data-jpa',
    moduleColor: 'green' as const,
    api: 'SensitiveAttributeCryptoService.isEncrypted (C044)',
    role: '암호문 여부 배지',
    snippet: 'crypto.isEncrypted(stored)',
  },
  {
    module: 'eraf-data-jpa',
    moduleColor: 'green' as const,
    api: 'GenericRepository.findAllActive (C042)',
    role: '테넌트 격리 페이지 조회',
    snippet: 'repo.findAllActive(spec, pageable)',
  },
  {
    module: 'eraf-data-jpa',
    moduleColor: 'green' as const,
    api: 'SortWhitelist + SearchSpecificationFactory (C041)',
    role: 'ORDER BY 인젝션 차단',
    snippet: 'factory.withSort(pageable, sortWhitelist)',
  },
  {
    module: 'eraf-core',
    moduleColor: 'blue' as const,
    api: 'ApiResponse + PageResponse (C002)',
    role: '목록 표준 응답',
    snippet: 'return ApiResponse.page(service.listSamples(...))',
  },
  {
    module: 'eraf-core',
    moduleColor: 'blue' as const,
    api: 'BusinessException (C001)',
    role: '미존재 테넌트 전환 차단',
    snippet: 'throw new BusinessException(TENANT_NOT_FOUND)',
  },
  {
    module: 'eraf-core',
    moduleColor: 'blue' as const,
    api: '@Audited(action="TENANT_SWITCHED") (C014)',
    role: '전환/생성 감사 기록',
    snippet: '@Audited(action="TENANT_SWITCHED", resourceId="#req.tenantId")',
  },
  {
    module: 'eraf-security',
    moduleColor: 'yellow' as const,
    api: '@HasPermission("TENANT_READ/SWITCH") (C030)',
    role: '메서드 권한 (이중 방어)',
    snippet: '@HasPermission("TENANT_SWITCH") switchTenant()',
  },
  {
    module: 'eraf-security',
    moduleColor: 'yellow' as const,
    api: 'SecurityChainCustomizer + PathPolicy (C029)',
    role: '/tenant-switcher/** 권한 정책',
    snippet: 'PathPolicy.hasPermission(..., "TENANT_READ")',
  },
  {
    module: 'eraf-security',
    moduleColor: 'yellow' as const,
    api: 'SecurityAuditLogger (C031)',
    role: '권한 거부 자동 기록',
    snippet: '(자동 — biz 코드 0줄)',
  },
  {
    module: 'eraf-swagger',
    moduleColor: 'blue' as const,
    api: '@ApiSuccessResponse (C037)',
    role: 'OpenAPI 응답 스키마',
    snippet: '@ApiSuccessResponse(TenantContextResponse.class)',
  },
];

/**
 * TenantSwitcherConsolePage — 테넌트 전환 콘솔 페이지 (/tenant-switcher)
 * 5상태: default / loading / empty / error / dialog
 * §6 UI 플로우, C046 멀티테넌시 + C049 읽기/쓰기 라우팅 + C044 민감 컬럼 시연
 */
export function TenantSwitcherConsolePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // 권한 확인
  const hasTenantRead = useHasPermission('TENANT_READ');
  const hasTenantSwitch = useHasPermission('TENANT_SWITCH');

  // 표본 목록 필터/페이지 상태
  const [sampleFilters, setSampleFilters] = useState<SampleSearchCondition>({
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    sort: DEFAULT_SORT,
  });

  // Dialog 상태
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogServerError, setDialogServerError] = useState<string | null>(null);

  // Toast 상태
  const [toast, setToast] = useState<string | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  // TanStack Query
  const contextQuery = useTenantContext();
  const tenantsQuery = useTenants();
  const routingQuery = useRoutingStatus();
  const samplesQuery = useTenantSamples(sampleFilters);
  const switchMutation = useSwitchTenant();
  const createMutation = useCreateSample();

  // 초기 로딩: context + tenants + routing 병렬
  const isInitialLoading =
    contextQuery.isLoading || tenantsQuery.isLoading || routingQuery.isLoading;

  // 최상위 403 오류 (TENANT_READ 미보유 — BE 응답)
  const contextErrorCode = (contextQuery.error as unknown as ApiError | null)?.code ?? null;
  const isGlobalError =
    contextQuery.isError &&
    (contextErrorCode === 'AUTH-403-001' || contextErrorCode?.startsWith('AUTH-'));

  // 테넌트 전환 핸들러
  const handleSwitchTenant = useCallback(
    (tenantId: string) => {
      switchMutation.mutate(
        { tenantId },
        {
          onSuccess: (data) => {
            showToast(t('tenantSwitcher.toast.switched', { tenant: data.currentTenantLabel || tenantId }));
            // 표본 목록 초기화
            setSampleFilters((prev) => ({ ...prev, page: 0 }));
          },
          onError: (err) => {
            const apiErr = err as unknown as ApiError;
            const errKey = `tenantSwitcher.error.${apiErr.code}`;
            showToast(t(errKey, { defaultValue: apiErr.message }));
          },
        }
      );
    },
    [switchMutation, showToast, t]
  );

  // 페이지 변경
  const handlePageChange = useCallback((newPage: number) => {
    setSampleFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  // 표본 추가 다이얼로그 열기/닫기
  const handleOpenDialog = useCallback(() => {
    setDialogServerError(null);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setDialogServerError(null);
  }, []);

  // 표본 생성 제출
  const handleCreateSample = useCallback(
    (values: TenantSampleCreateFormValues) => {
      setDialogServerError(null);
      createMutation.mutate(
        {
          label: values.label,
          category: values.category || undefined,
          secret: values.secret || undefined,
          note: values.note || undefined,
        },
        {
          onSuccess: () => {
            setDialogOpen(false);
            showToast(t('tenantSwitcher.toast.created'));
          },
          onError: (err) => {
            const apiErr = err as unknown as ApiError;
            const errKey = `tenantSwitcher.error.${apiErr.code}`;
            const msg = t(errKey, { defaultValue: apiErr.message });
            setDialogServerError(msg);
          },
        }
      );
    },
    [createMutation, showToast, t]
  );

  // TENANT_READ 미보유 → ErrorState (403)
  if (!hasTenantRead && !isInitialLoading) {
    return (
      <div style={{ padding: 'var(--spacing-6)' }}>
        <ErrorState
          title={t('tenantSwitcher.error.AUTH-403-001')}
          message={t('tenantSwitcher.error.AUTH-403-001')}
          actions={[
            {
              label: t('tenantSwitcher.error.home'),
              onClick: () => navigate('/'),
              variant: 'primary',
            },
          ]}
        />
      </div>
    );
  }

  // BE 403 오류
  if (isGlobalError) {
    return (
      <div style={{ padding: 'var(--spacing-6)' }}>
        <ErrorState
          title={t('tenantSwitcher.error.AUTH-403-001')}
          message={
            contextErrorCode
              ? t(`tenantSwitcher.error.${contextErrorCode}`, {
                  defaultValue: t('tenantSwitcher.error.network'),
                })
              : t('tenantSwitcher.error.loadFailed')
          }
          onRetry={() => void contextQuery.refetch()}
          actions={[
            {
              label: t('tenantSwitcher.error.home'),
              onClick: () => navigate('/'),
              variant: 'secondary',
            },
          ]}
        />
      </div>
    );
  }

  const currentTenant = contextQuery.data?.currentTenant ?? '';

  return (
    <div>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('tenantSwitcher.page.title')}
        breadcrumb={<span>{t('tenantSwitcher.page.breadcrumb')}</span>}
        actions={
          <TenantSelector
            context={contextQuery.data}
            tenants={tenantsQuery.data}
            isLoading={isInitialLoading}
            isSwitching={switchMutation.isPending}
            hasSwitchPermission={hasTenantSwitch}
            onSwitch={handleSwitchTenant}
          />
        }
      />

      {/* 2단 레이아웃: 라우팅 카드 (좌) + 표본 그리드 (우) */}
      <div className="tenant-switcher-layout" style={{ display: 'flex', gap: 'var(--spacing-4)', alignItems: 'flex-start', marginTop: 'var(--spacing-4)' }}>
        {/* 좌: 라우팅 상태 카드 (md 이상 고정 너비) */}
        <div className="tenant-switcher-routing-card" style={{ flexShrink: 0, width: 260 }}>
          <RoutingStatusCard
            data={routingQuery.data}
            isLoading={routingQuery.isLoading}
            isError={routingQuery.isError}
            onRetry={() => void routingQuery.refetch()}
          />
        </div>

        {/* 우: 표본 그리드 */}
        <SampleGrid
          data={samplesQuery.data}
          isLoading={samplesQuery.isLoading}
          isError={samplesQuery.isError}
          errorCode={(samplesQuery.error as unknown as ApiError | null)?.code ?? undefined}
          page={sampleFilters.page ?? 0}
          onPageChange={handlePageChange}
          onAddSample={handleOpenDialog}
          onRetry={() => void samplesQuery.refetch()}
        />
      </div>

      {/* 공통 모듈 시연 패널 (§6.8) */}
      <CommonUsagePanel
        items={COMMON_USAGE_ITEMS}
        title={t('tenantSwitcher.commonUsage.title')}
        subtitle={t('tenantSwitcher.commonUsage.description')}
      />

      {/* Dialog 상태 — 표본 추가 */}
      {dialogOpen && (
        <SampleFormDialog
          currentTenant={currentTenant}
          isPending={createMutation.isPending}
          serverError={dialogServerError}
          onSubmit={handleCreateSample}
          onClose={handleCloseDialog}
        />
      )}

      {/* Toast 알림 */}
      {toast !== null && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 1100,
            background: 'var(--color-sidebar-bg)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {toast}
        </div>
      )}

      {/* 반응형 + 애니메이션 */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          .tenant-switcher-layout {
            flex-direction: column !important;
          }
          .tenant-switcher-routing-card {
            width: 100% !important;
          }
        }
      `}</style>
    </div>
  );
}
